import { useCallback, useEffect } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { Box, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { useDisplayBrightness } from '@ocx/cap-display-brightness';
import { ModalDrawer } from '@ocx/ui-modals';
import { Barcode, Button, Note, SizingMultiplier, Typography } from '@ocx/ui';
import { InfoOutlinedIcon } from '@ocx/ui-icons';
import { useIsAppFocused } from '@ocx-app/components/blur/use-is-app-focused.hook';
import { buttonTextMessages } from '@ocx-app/modules/localization/button-text.messages';

import { messages } from './loyalty-id-barcode-scan-action-sheet.messages';
import { LoyaltyIdBarcodeScanActionSheetControllerProps } from './loyalty-id-barcode-scan-action-sheet.types';

const BARCODE_HEIGHT_PX = 100;

export const LoyaltyIdBarcodeScanActionSheetController = ({
  barcodeValue,
}: LoyaltyIdBarcodeScanActionSheetControllerProps) => {
  const modal = useModal();
  const setDisplayBrightness = useDisplayBrightness();
  const isAppFocused = useIsAppFocused();
  const { hide, remove, visible } = modal;

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (isAppFocused) {
      setDisplayBrightness('max');
      return;
    }

    setDisplayBrightness('initial');
  }, [isAppFocused, setDisplayBrightness, visible]);

  useEffect(() => {
    return () => {
      setDisplayBrightness('initial');
    };
  }, [setDisplayBrightness]);

  const handleEnter = useCallback(() => {
    setDisplayBrightness('max');
  }, [setDisplayBrightness]);

  const handleClose = useCallback(() => {
    setDisplayBrightness('initial');
    void hide();
  }, [hide, setDisplayBrightness]);

  return (
    <ModalDrawer open={visible} onEnter={handleEnter} onClose={handleClose} onExited={remove}>
      <Stack alignItems="justify" p={SizingMultiplier.lg}>
        <Box display="flex" justifyContent="center" mb={SizingMultiplier.md}>
          <Barcode content={barcodeValue} style={{ height: BARCODE_HEIGHT_PX }} />
        </Box>

        <Typography variant="h5" component="h2" gutterBottom>
          <FormattedMessage {...messages.title} />
        </Typography>
        <Typography>
          <FormattedMessage {...messages.description} />
        </Typography>

        <Stack
          direction="row"
          component={Note}
          my={SizingMultiplier.md}
          spacing={SizingMultiplier.sm}
          alignItems="center">
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Stack>
            <Typography variant="caption" color="textSecondary">
              <FormattedMessage {...messages.reminderTitle} />
            </Typography>
            <Typography variant="caption" color="textSecondary">
              <FormattedMessage {...messages.reminderDescription} />
            </Typography>
          </Stack>
        </Stack>

        <Button variant="text" onClick={handleClose}>
          <FormattedMessage {...buttonTextMessages['button-text:close']} />
        </Button>
      </Stack>
    </ModalDrawer>
  );
};
